// components/RegisterAssignmentModal.js
import { Modal } from './modal.ts';
import { 
  fetchRegisterDictionaries, 
  fetchDevices, 
  updateRegister,
  assignRegister, deleteRegister 
} from '../services/api.ts';
import { globalState } from "../framework/globalState.ts";

export class RegisterAssignmentModal {
  constructor(existingRegister = null) {
    this.existing = existingRegister;
    this.siteId = globalState.currentSite?._id;
    this.modal = new Modal();
  }

  async open() {
    if (!this.siteId) return alert('No site selected');

    const [dicts, devices] = await Promise.all([
      fetchRegisterDictionaries(),
      fetchDevices(this.siteId)
    ]);

    const html = this.render(dicts, devices);
    this.modal.open(html);
    this.bindEvents(dicts, devices);
  }

  render(dicts, devices) {
    const isEdit = !!this.existing;
    const title = isEdit ? 'Edit Register Assignment' : 'Create New Register';

    const hasDevice = this.existing?.deviceId && String(this.existing.deviceId).length === 24;
    const currentDevice = devices.find(d => d._id === this.existing?.deviceId);

    const deviceOptions = devices.map(d => `
      <option value="${d._id}" ${this.existing?.deviceId === d._id ? 'selected' : ''}>
        ${d.name} (${d.role.toUpperCase()}${d.type ? ' – ' + d.type : ''})
      </option>
    `).join('');

    const dictOptions = dicts.map(d => `
      <option value="${d.key}"
              data-unit="${d.defaultUnit || ''}"
              data-datatype="${d.defaultDataType}"
              data-access="${d.defaultAccess}"
              data-regtype="${d.defaultRegType}"
              data-scale="${d.defaultScaleFactor ?? 1}"
              ${this.existing?.dictionaryKey === d.key ? 'selected' : ''}>
        [${d.group || '—'}] ${d.displayName}
      </option>
    `).join('');

    return `
      <div class="register-assign-form">
        <div class="modal-header">
          <h3>${title}</h3>
        </div>
        <div class="modal-body">
          <form id="assign-form">
            <input type="hidden" name="registerId" value="${this.existing?._id || ''}">

            <div class="form-grid">
              <!-- DEVICE SELECTION -->
              <div class="full-width">
                <label>
                  Device 
                  ${hasDevice 
                    ? '<span style="color:#d73;">(Locked – cannot change)</span>' 
                    : '<span style="color:#28a745;">(Can assign now)</span>'
                  }
                </label>
                ${hasDevice ? `
                  <input type="text" value="${currentDevice?.name || '—'} (${currentDevice?.role.toUpperCase()})" readonly 
                         style="background:#f0f0f0; color:#666;">
                  <input type="hidden" name="deviceId" value="${this.existing.deviceId}">
                ` : `
                  <select name="deviceId" ${isEdit ? '' : 'required'}>
                    <option value="">-- Select Device (Optional for floating) --</option>
                    ${deviceOptions}
                  </select>
                  <small style="color:#666;">Leave empty to keep as floating register</small>
                `}
              </div>

              <!-- DICTIONARY PRESET -->
              <div class="full-width">
                <label>Register Preset *</label>
                <select name="dictionaryKey" required>
                  <option value="">-- Select Preset --</option>
                  ${dictOptions}
                </select>
              </div>

              <!-- MODBUS ADDRESS -->
              <div>
                <label>Modbus Address *</label>
                <input type="number" name="address" min="0" max="65535" required
                       value="${this.existing?.address || ''}" 
                       ${isEdit ? '' : 'placeholder="e.g. 40001"'}>
              </div>

              <!-- AUTO-FILLED FIELDS -->
              <div>
                <label>Name</label>
                <input type="text" name="name" readonly value="${this.existing?.name || ''}">
              </div>
              <div>
                <label>Unit</label>
                <input type="text" name="unit" name="unit" readonly>
              </div>
              <div>
                <label>Data Type</label>
                <input type="text" name="dataType" readonly>
              </div>
              <div>
                <label>Register Type</label>
                <select name="type">
                  ${['HOLDING','INPUT','COIL','DISCRETE'].map(t => 
                    `<option value="${t}" ${this.existing?.type === t ? 'selected' : ''}>${t}</option>`
                  ).join('')}
                </select>
              </div>
              <div>
                <label>Scale Factor</label>
                <input type="number" step="0.001" name="scaleFactor" readonly>
              </div>
              <div class="full-width">
                <label>Description</label>
                <textarea name="description">${this.existing?.description || ''}</textarea>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-primary">
                ${isEdit ? 'Update Register' : 'Create Register'}
              </button>
              <button type="button" class="btn-secondary modal-close">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  bindEvents(dicts, devices) {
    const form = this.modal.contentEl.querySelector('#assign-form');
    const dictSelect = form.querySelector('[name="dictionaryKey"]');
    const nameInput = form.querySelector('[name="name"]');
    const unitInput = form.querySelector('[name="unit"]');
    const dataTypeInput = form.querySelector('[name="dataType"]');
    const scaleInput = form.querySelector('[name="scaleFactor"]');

    // Auto-fill from dictionary
    dictSelect.onchange = () => {
      const opt = dictSelect.selectedOptions[0];
      if (!opt?.value) return;
      const dict = dicts.find(d => d.key === opt.value);
      if (!dict) return;
      nameInput.value = dict.displayName;
      unitInput.value = dict.defaultUnit || '';
      dataTypeInput.value = dict.defaultDataType || '';
      scaleInput.value = dict.defaultScaleFactor ?? 1;
    };

    if (this.existing?.dictionaryKey) {
      dictSelect.dispatchEvent(new Event('change'));
    }

    // FINAL BULLETPROOF SUBMIT LOGIC
    form.onsubmit = async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const data = Object.fromEntries(formData);

      try {
        if (this.existing) {
          // EDIT MODE — ALWAYS PRESERVE deviceId IF IT EXISTS
          const updatePayload = {
            dictionaryKey: data.dictionaryKey,
            address: Number(data.address),
            type: data.type,
            description: data.description || '',
          };

          // CRITICAL: Always include deviceId if register already has one
          if (this.existing.deviceId) {
            updatePayload.deviceId = this.existing.deviceId; // ← THIS WAS MISSING
          }
          // Only allow assigning device if it was floating before
          else if (data.deviceId) {
            updatePayload.deviceId = data.deviceId;
          }

          await updateRegister(this.existing._id, updatePayload);
          alert('Register updated successfully!');
        } else {
          // CREATE MODE
          await assignRegister(this.siteId, {
            deviceId: data.deviceId || null,
            dictionaryKey: data.dictionaryKey,
            address: Number(data.address),
            type: data.type,
            overrides: {
              name: nameInput.value,
              unit: unitInput.value,
              dataType: dataTypeInput.value,
              scaleFactor: Number(scaleInput.value),
              description: data.description
            }
          });
          alert('Register created successfully!');
        }

        this.modal.close();
        location.reload();
      } catch (err) {
        console.error(err);
        alert('Error: ' + (err.message || 'Failed to save register'));
      }
    };
  }
}